import { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, Volume2, VolumeX, AudioWaveform } from 'lucide-react';
import { capabilities } from '../data/mock';
import { speak, cancel, preloadVoices, onVoiceState } from '../lib/aiVoice';

export default function Capabilities() {
  const [voiceOn, setVoiceOn] = useState(true);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const hoverTimerRef = useRef(null);

  useEffect(() => {
    // Preload all 5 MP3s for instant playback
    const audioMap = {};
    capabilities.forEach((c, i) => {
      if (c.audio) audioMap[i] = c.audio;
    });
    preloadVoices(audioMap);
    const off = onVoiceState((state, idx) => {
      if (state === 'start') setSpeakingIdx(idx);
      else if (state === 'end') setSpeakingIdx(null);
    });
    return () => {
      off();
      cancel();
    };
  }, []);

  const handleEnter = (idx, audio) => {
    if (!voiceOn || !audio) return;
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      speak(audio, { idx, volume: 1 });
    }, 280);
  };

  const handleLeave = () => {
    clearTimeout(hoverTimerRef.current);
    cancel();
  };

  const toggleVoice = () => {
    if (voiceOn) cancel();
    setVoiceOn(!voiceOn);
  };

  return (
    <section id="services" className="relative px-5 md:px-10 lg:px-14 py-24 md:py-32 border-t border-white/5">
      <div className="flex items-center gap-4 mb-10">
        <span className="w-10 h-px bg-[#FF3D2E]" />
        <p className="text-[10px] md:text-[11px] tracking-[0.3em] text-white/70">02 / CAPABILITIES</p>
      </div>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
        <h2 className="font-display text-[8vw] md:text-[5vw] lg:text-[5rem] leading-[0.95] tracking-tight">
          <span className="text-white">One studio.</span>{' '}
          <span className="font-script text-[#D7FF3D]">Every layer.</span>
        </h2>

        {/* AI Voice toggle */}
        <button
          type="button"
          onClick={toggleVoice}
          className={`group relative inline-flex items-center gap-3 px-5 py-3 border text-[11px] tracking-[0.25em] transition-all w-fit ${
            voiceOn
              ? 'border-[#D7FF3D] bg-[#D7FF3D]/10 text-white'
              : 'border-white/25 text-white/75 hover:text-white hover:border-white/60'
          }`}
        >
          {voiceOn ? (
            <Volume2 className="w-4 h-4 text-[#D7FF3D]" />
          ) : (
            <VolumeX className="w-4 h-4" />
          )}
          <span>
            {voiceOn ? 'AI VOICE ON · HOVER A CARD' : 'ENABLE AI VOICE'}
          </span>
          {voiceOn && (
            <span className="flex items-end gap-[2px] h-3 ml-1">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="w-[2px] bg-[#D7FF3D] animate-pulse"
                  style={{
                    height: `${50 + i * 25}%`,
                    animationDelay: `${i * 150}ms`,
                    animationDuration: '900ms',
                  }}
                />
              ))}
            </span>
          )}
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-5 md:gap-6">
        {capabilities.map((cap, idx) => {
          const isLast = idx === capabilities.length - 1;
          const isSpeaking = speakingIdx === idx;
          return (
            <div
              key={cap.num}
              onMouseEnter={() => handleEnter(idx, cap.audio)}
              onMouseLeave={handleLeave}
              style={{ '--accent': cap.accent }}
              className={`group relative border border-white/10 bg-white/[0.02] hover:bg-[rgba(255,255,255,0.04)] hover:border-[var(--accent)] transition-[transform,border-color,background-color,box-shadow] duration-500 ease-out p-8 md:p-10 overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_25px_60px_-20px_var(--accent)] ${
                isSpeaking ? 'border-[var(--accent)] shadow-[0_25px_60px_-20px_var(--accent)] -translate-y-1.5' : ''
              } ${isLast ? 'md:col-span-2' : ''}`}
            >
              {/* Accent corner glow */}
              <span
                className={`pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl transition-opacity duration-700 ${
                  isSpeaking ? 'opacity-40' : 'opacity-0 group-hover:opacity-30'
                }`}
                style={{ background: `radial-gradient(circle, var(--accent), transparent 65%)` }}
              />
              {/* Animated top sweep bar */}
              <span
                className={`pointer-events-none absolute top-0 left-0 h-[2px] transition-all duration-700 ease-out ${
                  isSpeaking ? 'w-full' : 'w-0 group-hover:w-full'
                }`}
                style={{ backgroundColor: 'var(--accent)' }}
              />

              <div className="relative flex items-start justify-between mb-8">
                <span
                  className="font-display text-6xl md:text-7xl transition-all duration-500 ease-out group-hover:scale-110 group-hover:translate-x-1 leading-none"
                  style={{
                    WebkitTextStroke: '1px rgba(255,255,255,0.10)',
                    color: 'transparent',
                  }}
                >
                  <span className={isSpeaking ? 'hidden' : 'block group-hover:hidden'}>{cap.num}</span>
                  <span
                    className={isSpeaking ? 'block' : 'hidden group-hover:block'}
                    style={{
                      WebkitTextStroke: '0',
                      color: 'var(--accent)',
                      textShadow: '0 8px 30px var(--accent)',
                    }}
                  >
                    {cap.num}
                  </span>
                </span>
                <div className="flex items-center gap-3">
                  {isSpeaking && <Waveform color={cap.accent} />}
                  <ArrowUpRight className="w-5 h-5 text-white/40 transition-all duration-500 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:scale-125" />
                </div>
              </div>

              <h3 className="relative font-display text-3xl md:text-4xl tracking-tight text-white mb-4">
                {cap.title}
              </h3>
              <p className="relative text-white/65 group-hover:text-white/85 max-w-md leading-relaxed transition-colors duration-500">
                {cap.desc}
              </p>
              <div className="relative mt-8 flex flex-wrap gap-2">
                {cap.tags.map((t, i) => (
                  <span
                    key={t}
                    style={{ transitionDelay: `${i * 60}ms` }}
                    className="text-[10px] tracking-[0.25em] text-white/70 group-hover:text-white group-hover:border-[var(--accent)] group-hover:bg-[color-mix(in_srgb,var(--accent)_15%,transparent)] border border-white/15 px-3 py-1.5 transition-all duration-500"
                  >
                    {t}
                  </span>
                ))}
              </div>

              {/* Listen status badge */}
              {voiceOn && (
                <div className="relative mt-6 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] text-white/40 group-hover:text-white/80 transition-colors">
                  <AudioWaveform className="w-3.5 h-3.5" />
                  {isSpeaking ? 'SPEAKING…' : 'HOVER TO LISTEN'}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-12 flex flex-wrap items-center gap-6">
        <a
          href="#services"
          className="group inline-flex items-center gap-2 px-6 py-4 bg-[#FF3D2E] text-white text-[11px] tracking-[0.25em] hover:bg-[#ff5446] transition-colors"
        >
          VIEW ALL SERVICES
          <ArrowUpRight className="w-3.5 h-3.5 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
        </a>
        <p className="text-[11px] tracking-[0.3em] text-white/50">20+ CAPABILITIES · À LA CARTE OR FULL STACK</p>
      </div>
    </section>
  );
}

function Waveform({ color }) {
  return (
    <span className="flex items-end gap-[3px] h-5" aria-label="speaking">
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className="w-[3px] rounded-sm"
          style={{
            backgroundColor: color,
            animation: `wf 0.9s ease-in-out ${i * 80}ms infinite alternate`,
            height: '40%',
          }}
        />
      ))}
      <style>{`
        @keyframes wf {
          0% { height: 20%; }
          50% { height: 100%; }
          100% { height: 35%; }
        }
      `}</style>
    </span>
  );
}
